import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import StickyMobileCTA from "./components/StickyMobileCTA";
import PixelContactTracker from "./components/PixelContactTracker";

import Home from "./pages/Home";
import About from "./pages/About";
import Projects from "./pages/Projects";
import ServicesList from "./pages/ServicesList";
import Residential from "./pages/Residential";
import LandscapeDesign from "./pages/LandscapeDesign";
import PavingStones from "./pages/PavingStones";
import RetainingWalls from "./pages/RetainingWalls";
import FenceBuilders from "./pages/FenceBuilders";
import TreePlanting from "./pages/TreePlanting";
import SodInstallation from "./pages/SodInstallation";
import OutdoorLiving from "./pages/OutdoorLiving";
import Decks from "./pages/Decks";
import FireFeatures from "./pages/FireFeatures";
import Pergolas from "./pages/Pergolas";
import Hardscaping from "./pages/Hardscaping";
import SnowRemoval from "./pages/SnowRemoval";
import Commercial from "./pages/Commercial";
import ServiceDetail from "./pages/ServiceDetail";
import YardDesigner from "./pages/YardDesigner";
import Estimator from "./pages/Estimator";
import Blog from "./pages/Blog";
import Contact from "./pages/Contact";
import ServiceArea from "./pages/ServiceArea";
import Areas from "./pages/Areas";
import AreaService from "./pages/AreaService";
import Account from "./pages/Account";
import Estimate from "./pages/Estimate";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminLeads from "./pages/admin/Leads";
import AdminGenerations from "./pages/admin/Generations";
import AdminProjects from "./pages/admin/Projects";
import AdminBlog from "./pages/admin/Blog";
import AdminAreas from "./pages/admin/Areas";
import AdminServices from "./pages/admin/Services";
import AdminFounders from "./pages/admin/Founders";
import AdminTestimonials from "./pages/admin/Testimonials";
import AdminSettings from "./pages/admin/Settings";
import { Navigate } from "react-router-dom";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname]);
  return null;
}

function Shell() {
  const loc = useLocation();
  const hideChrome = loc.pathname.startsWith("/admin");
  const hideStickyCta = hideChrome || loc.pathname.startsWith("/yard-designer") || loc.pathname.startsWith("/estimator") || loc.pathname.startsWith("/estimate") || loc.pathname.startsWith("/contact") || loc.pathname.startsWith("/account");

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    window.__lenis = lenis;
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => { lenis.destroy(); window.__lenis = null; };
  }, []);

  return (
    <div className="App grain min-h-screen bg-[#151515] text-[#F5F3EF]">
      {!hideChrome && <Navbar />}
      <ScrollToTop />
      <PixelContactTracker />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/residential" element={<Residential />} />
        <Route path="/residential/landscape-design" element={<LandscapeDesign />} />
        <Route path="/residential/paving-stones" element={<PavingStones />} />
        <Route path="/residential/retaining-walls" element={<RetainingWalls />} />
        <Route path="/residential/fence-builders" element={<FenceBuilders />} />
        <Route path="/residential/tree-planting" element={<TreePlanting />} />
        <Route path="/residential/sod-installation" element={<SodInstallation />} />
        <Route path="/residential/outdoor-living" element={<OutdoorLiving />} />
        <Route path="/residential/decks" element={<Decks />} />
        <Route path="/residential/fire-features" element={<FireFeatures />} />
        <Route path="/residential/pergolas" element={<Pergolas />} />
        <Route path="/residential/hardscaping" element={<Hardscaping />} />
        <Route path="/outdoor-living" element={<OutdoorLiving />} />
        <Route path="/landscape-design" element={<LandscapeDesign />} />
        <Route path="/paving-stones" element={<PavingStones />} />
        <Route path="/retaining-walls" element={<RetainingWalls />} />
        <Route path="/fence-builders" element={<FenceBuilders />} />
        <Route path="/tree-planting" element={<TreePlanting />} />
        <Route path="/sod-installation" element={<SodInstallation />} />
        <Route path="/services/residential" element={<Residential />} />
        <Route path="/commercial" element={<Commercial />} />
        <Route path="/commercial/snow-removal" element={<SnowRemoval />} />
        <Route path="/snow-removal" element={<Navigate to="/commercial/snow-removal" replace />} />
        <Route path="/services/commercial" element={<Commercial />} />
        <Route path="/services/:category" element={<ServicesList />} />
        <Route path="/services/detail/outdoor-living" element={<Navigate to="/residential/outdoor-living" replace />} />
        <Route path="/services/detail/paver-patios" element={<Navigate to="/residential/paving-stones" replace />} />
        <Route path="/services/detail/retaining-walls" element={<Navigate to="/residential/retaining-walls" replace />} />
        <Route path="/services/detail/landscape-design" element={<Navigate to="/residential/landscape-design" replace />} />
        <Route path="/services/detail/decks" element={<Navigate to="/residential/decks" replace />} />
        <Route path="/services/detail/fire-features" element={<Navigate to="/residential/fire-features" replace />} />
        <Route path="/services/detail/pergolas" element={<Navigate to="/residential/pergolas" replace />} />
        <Route path="/services/detail/hardscaping" element={<Navigate to="/residential/hardscaping" replace />} />
        <Route path="/services/detail/:slug" element={<ServiceDetail />} />
        <Route path="/yard-designer" element={<YardDesigner />} />
        <Route path="/estimator" element={<Estimator />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<Blog />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/areas" element={<Areas />} />
        <Route path="/areas/:areaSlug/:serviceSlug" element={<AreaService />} />
        <Route path="/areas/:slug" element={<ServiceArea />} />
        <Route path="/account" element={<Account />} />
        <Route path="/estimate" element={<Estimate />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/leads" replace />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="generations" element={<AdminGenerations />} />
          <Route path="projects" element={<AdminProjects />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="areas" element={<AdminAreas />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="founders" element={<AdminFounders />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
      </Routes>
      {!hideChrome && <Footer />}
      {!hideChrome && <StickyMobileCTA />}
      <Toaster position="bottom-right" theme="dark" toastOptions={{ style: { background: "#0B2B1F", border: "1px solid rgba(216,214,208,0.2)", color: "#F5F3EF" } }} />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
